import { createContext, Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import qs from 'qs';
import { Store } from 'redux/root';

// redux
import { resetError } from 'redux/error-service/action';

// types
import {
	IFullTextSearchUsersForAdmin,
	ISearchUsersSort,
	SearchUsersSort,
	IUserResponse,
	OrganisationResponse,
	SEARCH_USER_BY_ADMIN_POSSIBLE_SEARCH,
	SortDirection,
	USERS_POSSIBLE_SORT,
	SearchUsersForAdminRequest,
} from '@joc/api-gateway';
import { SetPagination } from 'core/types';

// functions
import { setSearchOrSortQueryString } from 'core/functions';
import { resolveNextSort } from 'core/functions/sort';
import { getAllUserRolesInEachOrg } from '../EditUserCell/EditRolePopup/helpers';

// constants
import { TABLE_HEADERS, TABLE_ITEMS_CLASSNAMES, INITIAL_PAGINATION } from 'core/constants';

// components
import TableMain from 'shared/components/Table/TableMain';
import TableHead from 'shared/components/Table/TableHead';
import TableHeadCell from 'shared/components/Table/TableHead/TableHeadCell';
import TableHeadCellSortable from '../../../../shared/components/Table/TableHead/TableHeadCellSortable';
import TableBody from 'shared/components/Table/TableBody';
import TableRow from 'shared/components/Table/TableRow';
import AvatarCell from 'shared/components/Table/CellRenderers/AvatarCell';
import FullNameCell from 'shared/components/Table/CellRenderers/FullNameCell';
import OrgContactCell from 'shared/components/Table/CellRenderers/OrgContactCell';
import Loader from 'shared/components/Loader';
import { formatInteger } from 'core/functions/formatNumber';
import ResponseFailure from 'shared/components/ResponseFailure';
import TableMenu from 'components/Organization/TableMenu';
import RoleCell from 'shared/components/Table/CellRenderers/RoleCell';
import Observer from 'shared/components/Observer';
import EditUserCell from '../EditUserCell';

// styles
import styles from './UsersTable.module.scss';

type UserTableDataContextType = {
	setIsNeedCloseModal?: Dispatch<SetStateAction<boolean>>;
	isNeedCloseModal?: boolean;
	userData: IUserResponse;
};

export const UserTableDataContext = createContext<UserTableDataContextType>(
	null as unknown as UserTableDataContextType
);

export interface ISimpleRoleResponse {
	roleName: string;
	roleId?: number;
}

export interface ISortedRolesItem {
	organizationId?: string;
	organization?: OrganisationResponse;
	roleList: Array<ISimpleRoleResponse>;
}

type UsersTableParentProps = {
	setPagination: SetPagination;
	isLoading: boolean;
};

const UsersTable: FC<UsersTableParentProps> = ({ setPagination, isLoading }) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();
	const printRef = useRef<HTMLTableElement>(null);
	const [isNeedCloseModal, setIsNeedCloseModal] = useState<boolean>(false);
	const [sort, setSort] = useState<SearchUsersSort>();

	const companiesUsersList = useSelector((store: Store) => store.companiesUsersRedux.records);
	const companiesUsersTotal = useSelector((store: Store) => store.companiesUsersRedux.total);
	const errorState = useSelector((store: Store) => store.errorRedux.error.state);
	const errorMessage = useSelector((store: Store) => store.errorRedux.error.message);

	const [isClearSearch, setIsClearSearch] = useState<boolean>(false);

	useEffect(() => {
		const searchParams = qs.parse(location.search, {
			ignoreQueryPrefix: true,
		}) as unknown as SearchUsersForAdminRequest;
		setSort(searchParams.sort);
		setIsClearSearch(!searchParams.fullTextSearch?.value);
	}, [location.search]);

	const buttonFailureClickHandler = async () => {
		dispatch(resetError());
		history.push({ search: '' });
		setPagination(INITIAL_PAGINATION);
	};

	const appendQueryString = (
		newQueryParams?: IFullTextSearchUsersForAdmin | ISearchUsersSort,
		searchBy?: string
	): void => {
		const searchQuery = setSearchOrSortQueryString(location.search, newQueryParams, searchBy);
		const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search;
		if (searchQuery !== currentSearch) {
			history.push({ search: searchQuery });
		}
	};

	const searchUsersChangeHandler = (value: string): void => {
		const trimmedValue = value.trim();
		if (trimmedValue.length) {
			const fullTextSearchParams: IFullTextSearchUsersForAdmin = {
				value: trimmedValue,
				fields: Object.values(SEARCH_USER_BY_ADMIN_POSSIBLE_SEARCH),
			};
			appendQueryString(fullTextSearchParams, 'fullTextSearch');
		} else {
			const searchParams = qs.parse(location.search, { ignoreQueryPrefix: true }) as Record<string, unknown>;
			if (searchParams.fullTextSearch) {
				delete searchParams.fullTextSearch;
				history.push({ search: qs.stringify(searchParams) });
			}
		}
	};

	const handleSortChange = useCallback(
		(sortBy: USERS_POSSIBLE_SORT, clickedDirection?: SortDirection) => {
			setSort(prevSort => {
				const nextSort = resolveNextSort({
					currentSort: prevSort,
					nextSortBy: sortBy,
					clickedDirection,
					allowClear: true,
				});

				if (!nextSort) {
					const searchParams = qs.parse(location.search, { ignoreQueryPrefix: true });
					delete searchParams.sort;
					history.replace({ search: qs.stringify(searchParams) });
					return undefined;
				}

				appendQueryString(nextSort, 'sort');
				return SearchUsersSort.fromJS(nextSort);
			});
		},
		[history, location.search]
	);

	return (
		<div className={styles.container}>
			<div className={styles.table}>
				<TableMenu
					searchChangeHandler={searchUsersChangeHandler}
					isClearSearch={isClearSearch}
					setIsClearSearch={setIsClearSearch}
				/>
				{(errorState && !isLoading) || (!isLoading && !companiesUsersList.length) ? (
					<ResponseFailure
						styleTable={true}
						message={errorMessage || 'We couldn`t find the volunteers you were looking for this time.'}
						buttonClickHandler={buttonFailureClickHandler}
						buttonTitle="Get all"
					/>
				) : !companiesUsersList.length && isLoading ? (
					<div className={styles.loader}>
						<Loader />
					</div>
				) : (
					<TableMain ref={printRef}>
						<TableHead className={styles.table__header} callChild="users">
							<TableHeadCell
								itemClassName={TABLE_ITEMS_CLASSNAMES.avatar}
								text={TABLE_HEADERS.avatar}
								isSortDisabled={true}
							/>
							<TableHeadCellSortable
								title={TABLE_HEADERS.fullName}
								sortBy={USERS_POSSIBLE_SORT.FirstName}
								handleSortChange={handleSortChange}
								sort={sort}
							/>
							<TableHeadCellSortable
								title={TABLE_HEADERS.contacts}
								sortBy={USERS_POSSIBLE_SORT.PhoneNumber}
								handleSortChange={handleSortChange}
								sort={sort}
							/>
							<TableHeadCell
								itemClassName={TABLE_ITEMS_CLASSNAMES.coins}
								text={TABLE_HEADERS.coins}
								isSortDisabled={true}
							/>
							<TableHeadCell
								itemClassName={TABLE_ITEMS_CLASSNAMES.organizations}
								text={TABLE_HEADERS.organizations}
								isSortDisabled={true}
							/>
							<TableHeadCell itemClassName={TABLE_ITEMS_CLASSNAMES.userOptions} isSortDisabled={true} />
						</TableHead>
						<TableBody>
							{companiesUsersList.map((user, index) => (
								<div key={`${user.id}_${index}`}>
									<TableRow key={`${user.id}_${index}`} callChild="users">
										<AvatarCell imagePath={user.imagePath} user={user} withPopup={true} />
										<FullNameCell firstName={user.firstName} lastName={user.lastName} crop={true} />
										<OrgContactCell phone={user.phoneNumber} email={user.email} />
										<div style={{ display: 'flex', alignItems: 'center' }}>
											<span>{formatInteger(user.coins)}</span>
										</div>
										<RoleCell
											parentClassName={TABLE_ITEMS_CLASSNAMES.organizations}
											roles={getAllUserRolesInEachOrg(user.roles)}
										/>
										<UserTableDataContext.Provider
											value={{ isNeedCloseModal, setIsNeedCloseModal, userData: user }}
										>
											<EditUserCell />
										</UserTableDataContext.Provider>
									</TableRow>
								</div>
							))}
						</TableBody>
					</TableMain>
				)}
				{!!companiesUsersList.length && companiesUsersList.length < companiesUsersTotal && !errorState && (
					<Observer
						paginationSkip={companiesUsersList.length}
						setPagination={setPagination}
						isLoading={isLoading}
					/>
				)}
			</div>
		</div>
	);
};

export default UsersTable;
